import { useState, useEffect } from 'react'
import { functionsClient } from '../../../lib/functionsClient'
import type { Case } from '../../../hooks/useCase'

export interface UseScanControlsResult {
  isStartingScan: boolean
  isScanStarted: boolean
  isCanceling: boolean
  handleStartScan: () => Promise<void>
  handleCancelScan: () => Promise<void>
}

export function useScanControls(
  caseId: string | undefined,
  caseData: Case | null
): UseScanControlsResult {
  const [isStartingScan, setIsStartingScan] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isScanStarted, setIsScanStarted] = useState(false)

  useEffect(() => {
    if (caseData?.documentScan?.status === 'scanning') {
      setIsScanStarted(false)
    }
  }, [caseData?.documentScan?.status])

  // Start scanning - just trigger API, Firestore will update status
  const handleStartScan = async () => {
    if (!caseId || !caseData) return
    setIsScanStarted(false)
    setIsStartingScan(true)

    try {
      await functionsClient.startDocumentScan(
        caseId,
        caseData.loanType,
        'w2_employed'
      )

      // Success - Firestore will update via webhook, no local state to set
      setIsScanStarted(true)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to start scan'
      // Log error - Firestore should reflect failure state via webhook
      console.error('Start scan failed:', errorMsg)
    } finally {
      setIsStartingScan(false)
    }
  }

  // Cancel scanning - requires confirmation
  const handleCancelScan = async () => {
    if (!caseId) return

    setIsCanceling(true)

    try {
      await functionsClient.cancelScan(caseId, 'Cancelled by user')
      // Success - Firestore will update via webhook
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to cancel scan'
      console.error('Cancel scan failed:', errorMsg)
    } finally {
      setIsCanceling(false)
    }
  }

  return {
    isStartingScan,
    isScanStarted,
    isCanceling,
    handleStartScan,
    handleCancelScan,
  }
}

